import { Drawer, DrawerOverlay, DrawerProps, useToast } from '@chakra-ui/react';
import {
  usePostAfeTransactionMutation,
  usePostAfeMutation,
  usePutAfeMutation,
} from 'api';
import { AfeResponse } from 'api/bindings/AfeResponse';
import { PostAfeRequest } from 'api/bindings/PostAfeRequest';
import { PostAfeTransactionRequest } from 'api/bindings/PostAfeTransactionRequest';
import { PutAfeRequest } from 'api/bindings/PutAfeRequest';
import {
  AfeCreateFormDrawerContent,
  AfeUpdateFormDrawerContent,
  AfeFormReviewDrawerContent,
  AfeFormRevisionDrawerContent,
} from 'features';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

export interface AfeFormWorkflowDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  afe: AfeResponse | undefined;
  initialStage?: AfeFormWorkflowStage;
}

export enum AfeFormWorkflowStage {
  afe = 'afe',
  revision = 'revision',
  review = 'review',
}

// Union type for AFE form requests
export type AfeFormRequest = PostAfeRequest | PutAfeRequest;

// Type guard functions
const isPutAfeRequest = (afe: AfeFormRequest): afe is PutAfeRequest => {
  return 'id' in afe;
};

interface Form {
  currentStage: AfeFormWorkflowStage;
  afe: AfeFormRequest;
  revision: (PostAfeTransactionRequest & { entityId: string }) | undefined;
}

export const AfeFormWorkflowDrawer = (props: AfeFormWorkflowDrawerProps) => {
  const { afe, initialStage = AfeFormWorkflowStage.afe, ...rest } = props;

  // Determine if this is create or update based on whether afe has an id
  const isUpdate = afe?.id !== undefined && BigInt(afe.id) > 0;

  // Fetches
  const [postAfe] = usePostAfeMutation();
  const [putAfe] = usePutAfeMutation();
  const [postRevision] = usePostAfeTransactionMutation();

  // Create appropriate default values based on whether this is create or update
  const defaultAfe: AfeFormRequest = useMemo(() => {
    if (isUpdate && afe) {
      // For update, convert Afe to PutAfeRequest
      return {
        id: afe.id,
        name: afe.name,
        description: afe.description,
        year: afe.year,
        assigneeUserId: afe.assigneeUserId,
        budgetStart: afe.budgetStart,
        budgetEnd: afe.budgetEnd,
        estimatedStartDate: afe.estimatedStartDate,
        estimatedEndDate: afe.estimatedEndDate,
        actualStartDate: afe.actualStartDate,
        actualEndDate: afe.actualEndDate,
      } as PutAfeRequest;
    } else {
      // For create, provide empty PostAfeRequest structure
      return {
        clientId: '0', // This should be set from context/props
        name: '',
        description: '',
        year: new Date().getFullYear(),
        assigneeUserId: '0',
        budgetStart: null,
        budgetEnd: null,
        estimatedStartDate: null,
        estimatedEndDate: null,
        actualStartDate: null,
        actualEndDate: null,
      } as PostAfeRequest;
    }
  }, [afe, isUpdate]);

  const toast = useToast();
  const { control, handleSubmit, setValue, watch } = useForm<Form>({
    defaultValues: {
      afe: defaultAfe,
      currentStage: initialStage,
      revision: undefined,
    },
  });

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        let afeId = afe?.id ?? '0';

        if (initialStage === AfeFormWorkflowStage.afe) {
          let result;
          if (isPutAfeRequest(form.afe)) {
            result = await putAfe(form.afe);
          } else {
            result = await postAfe(form.afe);
          }

          if ('error' in result) {
            toast({
              title: `Failed to post AFE ${form.afe.name}`,
              status: 'error',
            });
            throw result.error;
          }
          afeId = result.data.id;
          toast({
            title: `AFE ${form.afe.name} posted!`,
            status: 'success',
          });
        }
        if (form.revision) {
          form.revision.entityId = afeId;
          const result = await postRevision({
            ...form.revision,
            userId: null,
          });
          if ('error' in result) {
            toast({
              title: `Failed to post revision ${form.revision.name} to AFE ${form.afe.name}`,
              status: 'error',
            });
            throw result.error;
          }
          toast({
            title: `Revision ${form.revision.name} posted to AFE ${form.afe.name}!`,
            status: 'success',
          });
        }

        props.onClose();
      } catch {
        // TODO
      }
    },
    [afe?.id, initialStage, props, toast, putAfe, postAfe, postRevision]
  );

  const formData = watch();
  const component = useMemo(() => {
    const { currentStage, afe } = formData;

    switch (currentStage) {
      case AfeFormWorkflowStage.afe:
        return (
          <Controller
            control={control}
            name="afe"
            render={(p) => {
              if (isUpdate && isPutAfeRequest(afe)) {
                return (
                  <AfeUpdateFormDrawerContent
                    afe={afe}
                    onClose={props.onClose}
                    onSubmit={(f) => {
                      p.field.onChange(f);
                      const nextStage = f.includeRevision
                        ? AfeFormWorkflowStage.revision
                        : AfeFormWorkflowStage.review;
                      setValue('currentStage', nextStage);
                    }}
                  />
                );
              } else {
                return (
                  <AfeCreateFormDrawerContent
                    onClose={props.onClose}
                    onSubmit={(f) => {
                      p.field.onChange(f);
                      const nextStage = f.includeRevision
                        ? AfeFormWorkflowStage.revision
                        : AfeFormWorkflowStage.review;
                      setValue('currentStage', nextStage);
                    }}
                  />
                );
              }
            }}
          />
        );
      case AfeFormWorkflowStage.revision:
        return (
          <Controller
            control={control}
            name="revision"
            render={(p) => (
              <AfeFormRevisionDrawerContent
                afeId={isPutAfeRequest(afe) ? afe.id : undefined}
                onClose={props.onClose}
                onSubmit={(f) => {
                  p.field.onChange(f);
                  setValue('currentStage', AfeFormWorkflowStage.review);
                }}
              />
            )}
          />
        );
      case AfeFormWorkflowStage.review:
        return (
          <AfeFormReviewDrawerContent
            initialStage={initialStage}
            afe={formData.afe}
            revision={formData.revision}
            onClose={props.onClose}
            onSubmit={(f) => {
              if (f.nextStage) {
                setValue('currentStage', f.nextStage);
              } else {
                return handleSubmit(onSubmit)();
              }
            }}
          />
        );
    }
  }, [
    control,
    formData,
    handleSubmit,
    initialStage,
    onSubmit,
    props.onClose,
    setValue,
    isUpdate,
  ]);

  return (
    <Drawer size="xl" {...rest}>
      <DrawerOverlay />
      {component}
    </Drawer>
  );
};
